import dayjs from "dayjs";
import parse from "html-react-parser";

import { Box, Grid, Typography, useTheme } from "@mui/material";

import {
  DefaultPaper,
  ProductFootprintCategoryAvatar
} from "@aclymatepackages/atoms";
import { formatDate, formatDecimal } from "@aclymatepackages/formatters";
import { displayProductFootprintCategories } from "@aclymatepackages/add-ons";
import { ProductFootprintPieChart } from "@aclymatepackages/modules";

import GrayPaper from "../../atoms/GrayPaper";
import ReportPagePaper from "../../modules/reporting/ReportPagePaper";
import ReportLayout from "../../layouts/ReportLayout";

import { useAccountData } from "../../../helpers/firebase";
import useProductsEmissionsFactors from "../../../helpers/contexts/ProductsEmissionsFactors";

const CompanyLogo = ({ width }) => {
  const [{ name: companyName, branding }] = useAccountData();
  const { logoUrl } = branding || {};

  return (
    <>
      {logoUrl ? (
        <img src={logoUrl} alt="Company Logo" width={width} crossOrigin="anonymous" />
      ) : (
        <Typography variant="h1" color="textPrimary">
          {companyName}
        </Typography>
      )}
    </>
  );
};

const ReportFooter = () => (
  <Box flexGrow={0} p={2}>
    <Grid container justifyContent="space-between" alignItems="flex-end">
      <Grid item>
        <Typography variant="h5" color="textPrimary">
          Prepared By Aclymate
        </Typography>
        <Typography variant="body2" color="textSecondary">
          http://aclymate.com | &copy; {dayjs().year()} Aclymate
        </Typography>
      </Grid>
      <Grid item>
        <img
          alt="aclymate-logo"
          src="/images/aclymate-full-logo.png"
          width={200}
        />
      </Grid>
    </Grid>
  </Box>
);

const ReportPage = ({ pdfLoading, children }) => {
  const { palette } = useTheme();

  return (
    <ReportPagePaper
      backgroundColor={palette.backgroundGray.main}
      pdfLoading={pdfLoading}
    >
      <Box display="flex" flexDirection="column">
        <Box flexGrow={0} p={2} style={{ backgroundColor: "white" }}>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Typography variant="h5" color="textPrimary">
                Aclymate Carbon Emissions Report
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Generated on {formatDate(new Date())}
              </Typography>
            </Grid>
            <Grid item>
              <CompanyLogo width={50} />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box flexGrow={1} p={4}>
        {children}
      </Box>
      <ReportFooter />
    </ReportPagePaper>
  );
};

const ReportPaperBox = ({ title, subtitle, children }) => (
  <DefaultPaper elevation={0}>
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Typography variant="h4" color="textPrimary">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </Grid>
      <Grid item>{children}</Grid>
    </Grid>
  </DefaultPaper>
);

const TitlePage = ({ pdfLoading, product }) => {
  const { name: productName, sku, imageUrl } = product;

  const [{ name: companyName }] = useAccountData();

  return (
    <ReportPagePaper pdfLoading={pdfLoading}>
      <Box
        display="flex"
        flexDirection="column"
        style={{ height: "100%", width: "100%" }}
      >
        <Box p={8} flexGrow={1}>
          <Grid container direction="column" spacing={8}>
            <Grid item container justifyContent="center">
              <Grid item>
                <CompanyLogo width={200} />
              </Grid>
            </Grid>
            <Grid item>
              <Box>
                <Typography variant="h1" color="textPrimary" align="center">
                  Product Carbon Footprint
                </Typography>
                <Typography variant="h4" color="textSecondary" align="center">
                  Prepared for {companyName} by Aclymate
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <GrayPaper boxStyle={{ padding: 0 }}>
                <Box p={4}>
                  <Grid container justifyContent="space-between">
                    {[
                      { title: "Product Name:", value: productName },
                      { title: "Product ID:", value: sku }
                    ].map(({ title, value }) => (
                      <Grid item>
                        <Typography variant="subtitle1" color="textSecondary">
                          {title}
                        </Typography>
                        <Typography variant="h3" color="textPrimary">
                          {value}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                <Box p={0.5} style={{ backgroundColor: "white" }} />
                <Box p={4} display="flex" justifyContent="center">
                  <Box>
                    <img
                      src={
                        imageUrl ||
                        "/images/platform/product-report-default.png"
                      }
                      alt={productName}
                      style={{
                        width: "200px",
                        objectFit: "cover"
                      }}
                    />
                  </Box>
                </Box>
              </GrayPaper>
            </Grid>
          </Grid>
        </Box>
        <ReportFooter />
      </Box>
    </ReportPagePaper>
  );
};

const ProductFootprintCategoryRow = ({
  category,
  kgs,
  percentage,
  label,
  annotation
}) => {
  const { palette } = useTheme();

  const [{ isSubscribed }] = useProductsEmissionsFactors();

  const { icon, color, name } =
    displayProductFootprintCategories.find(
      ({ dataKey }) => dataKey === category
    ) || {};

  return (
    <Grid item>
      <Box
        borderRadius={1}
        style={{
          width: "100%",
          border: `1px solid ${palette.backgroundGray.main}`,
          position: "relative"
        }}
        p={1}
      >
        {annotation && (
          <Box
            style={{ position: "absolute", bottom: -22, left: -10, zIndex: 1 }}
          >
            <Typography variant="h3">{annotation}</Typography>
          </Box>
        )}
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item>
            <Grid container spacing={2} flexWrap="nowrap" alignItems="center">
              {!label && (
                <Grid item>
                  <ProductFootprintCategoryAvatar
                    icon={icon}
                    color={color}
                    name={name}
                  />
                </Grid>
              )}
              <Grid item>
                <Typography variant={label ? "h5" : "h5"} color="textPrimary">
                  {name || label}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Typography variant="h6" color="textPrimary">
              {isSubscribed ? (
                formatDecimal(kgs)
              ) : (
                <span style={{ filter: "blur(4px" }}>###</span>
              )}{" "}
              kgs
              {percentage && ` (${formatDecimal(percentage)}%)`}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

const ProductSummaryPage = ({ product, pdfLoading }) => {
  const { kgsCo2e, footprintBreakdownKgs, reportSummary = "" } = product;

  const [{ isSubscribed }] = useProductsEmissionsFactors();

  const footprintBreakdownArray = Object.entries(footprintBreakdownKgs)
    .map(([category, kgs]) => {
      const percentage = (kgs / kgsCo2e) * 100;
      return { category, kgs, percentage };
    })
    .filter(({ kgs }) => !!kgs);

  const sortedCategories = footprintBreakdownArray.sort(
    (a, b) => b.kgs - a.kgs
  );

  const formatReportSummary = () => {
    if (!isSubscribed) {
      const scrubbedSummary = reportSummary.replaceAll(
        /\d+\.?\d*/g,
        `<span style="filter: blur(4px)">###</span>`
      );
      return parse(scrubbedSummary);
    }

    return parse(reportSummary);
  };

  return (
    <ReportPage pdfLoading={pdfLoading}>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <ReportPaperBox title="Product Footprint Summary">
            <Typography variant="body2" color="textSecondary">
              {formatReportSummary()}
            </Typography>
          </ReportPaperBox>
        </Grid>
        <Grid item>
          <ReportPaperBox
            title="Footprint Breakdown"
            subtitle={` The product's footprint is divided into ${footprintBreakdownArray.length} life cycle categories.`}
          >
            <Grid container>
              <Grid item xs={6}>
                <Grid container direction="column" spacing={1}>
                  {sortedCategories.map(
                    ({ category, kgs, percentage }, idx) => (
                      <ProductFootprintCategoryRow
                        key={category}
                        category={category}
                        kgs={kgs}
                        percentage={percentage}
                        annotation={
                          idx === sortedCategories.length - 1 ? "=" : "+"
                        }
                      />
                    )
                  )}
                  <Grid item>
                    <ProductFootprintCategoryRow
                      label="Total Product Footprint"
                      kgs={kgsCo2e}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Box
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center"
                  }}
                >
                  <ProductFootprintPieChart
                    footprintBreakdownKgs={footprintBreakdownKgs || {}}
                    totalFootprintKgs={kgsCo2e || 0}
                    blurValues={!isSubscribed}
                  />
                </Box>
              </Grid>
            </Grid>
          </ReportPaperBox>
        </Grid>
      </Grid>
    </ReportPage>
  );
};

const MethodologyPage = ({ pdfLoading }) => {
  const categoriesParagraph = `This approach looks at all major stages of a product’s life cycle:<br/>
  ${displayProductFootprintCategories
    .map(
      ({ name, description }, idx) =>
        `${idx + 1}: <strong>${name}</strong> - ${description}`
    )
    .join("<br/>")}.
  `;

  const paragraphs = [
    "Aclymate uses Life Cycle Assessment (LCA) methods in alignment with the Greenhouse Gas Protocol and ISO 14067 standards. This methodology was developed by IPPAG and is licensed by Aclymate. Unless otherwise indicated, all numbers are in line with IPPAG’s methodology.",
    parse(categoriesParagraph),
    "For each stage, we combine primary data (supplier and product specifications) with secondary data (industry-standard databases such as Ecoinvent) to estimate emissions. All results are expressed in kilograms of carbon dioxide equivalent (kg CO₂e), a common unit that allows comparison across greenhouse gases. "
  ];

  return (
    <ReportPage pdfLoading={pdfLoading}>
      <ReportPaperBox title="How We Calculate Product Carbon Footprints">
        <Grid container spacing={1} direction="column">
          {paragraphs.map((paragraph, idx) => (
            <Grid item key={`paragraph-${idx}`}>
              <Typography variant="body2">{paragraph}</Typography>
            </Grid>
          ))}
        </Grid>
      </ReportPaperBox>
    </ReportPage>
  );
};

const ProductReportPages = ({ product, pdfLoading }) => {
  const pages = [TitlePage, ProductSummaryPage, MethodologyPage];

  return (
    <>
      {pages.map((Page, index) => (
        <Page key={index} product={product} pdfLoading={pdfLoading} />
      ))}
    </>
  );
};

const ProductReport = ({
  setShowReport,
  companyName,
  product,
  isFinalized
}) => {
  const { name, sku } = product;
  return (
    <ReportLayout
      setShowReport={setShowReport}
      ReportComponent={ProductReportPages}
      initialName={`${formatDate(
        new Date()
      )} ${companyName} ${name}(${sku}) Product Emissions`}
      reportProps={{ product }}
      isFinalized={isFinalized}
    />
  );
};
export { ProductReportPages };
export default ProductReport;
